import React, {useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {Button, Col, Container, Row} from 'react-bootstrap';
import {useParams, useLocation} from "react-router-dom";
import {
	ConfirmationDialog
} from "../../Common/ConfirmationDialog";
import {
	GenericModelViewer
} from "../../Common/GenericModelViewer";
import {
    Glyphicon
} from "../../Common/GlyphIcon";
import {
    GuidUtils,
    ObjectUtils,
} from "../../../Utils";
import {useChangePage} from "../../../Hooks/NavigationHooks";
import {service as ContactService} from '../../../Services/ContactService';
import {service as ProjectService} from '../../../Services/ProjectService';
import {service as RelationshipService} from '../../../Services/RelationshipService';
import {usePrimeCache} from '../../../Hooks/ServiceHooks';
import {
	Stakeholder
} from "../../../Models";
import {
    useCheckAuth,
} from "../../../Hooks/RouteHooks";
import {
    useMakePageRequest
} from "../../../Hooks/RequestHooks";
import {
    useEditContextNodeId,
    usePermission
} from "../../../Hooks/PermissionHooks";

import {useContextIndependentPage} from "../../../Hooks/ContextBuilderHooks";
import {useClassNames} from "../../../Hooks/StyleHooks";
import {useValidations} from "../../../Hooks/GenericModelHooks";

let model = new Stakeholder();


export const ContactsViewPage = () => {
    useCheckAuth();
    useContextIndependentPage();

    const classNames = useClassNames('sb-contacts-view-page');
    const makePageRequest = useMakePageRequest();
    const permission = usePermission();
    const editContextNodeId = useEditContextNodeId();
    const changePage = useChangePage();

    const currentUser = useSelector(state => state.userState.user);
    const currentContext = useSelector(state => state.contextState.context);

    const [contact, setContact] = useState(null);
    const [existingContact, setExistingContact] = useState(null);
    const [validations, setValidations, isValid] = useValidations();
    const [editMode, setEditMode] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);

    const {conId} = useParams();
    const contactId = conId;
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const defaultRelationshipName = searchParams.get('relationship') ?? 'Landowner';
    const fromSurvey = searchParams.get('fromSurvey') === '1';
    const editOnOpen = searchParams.get('edit') === '1';
    const prefillFirstName = searchParams.get('firstName') || null;
    const prefillLastName = searchParams.get('lastName') || null;

    usePrimeCache([RelationshipService]);

    useEffect(() => {
        loadData();
    }, [contactId, currentContext?.project, currentUser?.organizationId]);

    const loadData = () => {
        if(contactId === "new") {

            //if we have a user pulled. let's default to their orgId.
            let userOrganizationId = currentUser?.organizationId;

            if(currentContext?.project) {
                //if a project is in context, lets get it and use that to populate that the relationship.
                makePageRequest(
                    () => ProjectService.GetProject(currentContext.project),
                    (x) => {
                        //if we have a project. We know the orgId to use.
                        const { projectId, contextNodeId, organizationId } = x.result;
                        let defaultProjectStakeholderRelationships = [];

                        // if the current context is editable, then prepopulate a default
                        // ProjectStakeholderRelationship for it
                        if(editContextNodeId(contextNodeId)) {
                            const defaultRelationshipId = RelationshipService.CachedValueByProperty(defaultRelationshipName, 'name')?.result?.relationshipId;
                            defaultProjectStakeholderRelationships.push(
                                {
                                    _tempId: GuidUtils.Generate(),
                                    projectId,
                                    contextNodeId,
                                    relationshipId: defaultRelationshipId,
                                }
                            )
                        }

                        setContact({
                            stakeholderTypeId: 1,
                            firstName: prefillFirstName,
                            lastName: prefillLastName,
                            ProjectStakeholderRelationship: defaultProjectStakeholderRelationships,
                            ...contact,
                            // use the user's orgId only if there is not a relationship
                            // already assigned for a different organizationId
                            organizationId: organizationId ?? userOrganizationId,
                        });
                        setEditMode(true);
                    }
                );
            } else {
                //else create an empty contact
                setContact({
                    organizationId: userOrganizationId,
                    stakeholderTypeId: 1,
                    firstName: prefillFirstName,
                    lastName: prefillLastName,
                });
                setEditMode(true);
            }
        } else {
            // don't reload if we haven't changed
            if(contactId === contact?.contactId) {
                return;
            }

            //fetch contact and load that data
            makePageRequest(
                () => ContactService.Get(contactId, false),
                (x) => {
                    let loaded = x.result;

                    // When opening an existing contact in edit mode from the survey's
                    // contact_relationship question (relationship + fromSurvey URL params),
                    // prepopulate a ProjectStakeholderRelationship for the current project
                    // so that saving alone is enough to assign the relationship.
                    if (editOnOpen && fromSurvey && currentContext?.project) {
                        const relationshipId = RelationshipService.CachedValueByProperty(defaultRelationshipName, 'name')?.result?.relationshipId;
                        if (relationshipId) {
                            makePageRequest(
                                () => ProjectService.GetProject(currentContext.project),
                                (proj) => {
                                    const { projectId, contextNodeId } = proj.result;
                                    const alreadyHas = (loaded.ProjectStakeholderRelationship ?? [])
                                        .some(psr =>
                                            psr.projectId === projectId &&
                                            psr.relationshipId === relationshipId
                                        );
                                    if (!alreadyHas && editContextNodeId(contextNodeId)) {
                                        loaded = {
                                            ...loaded,
                                            ProjectStakeholderRelationship: [
                                                ...(loaded.ProjectStakeholderRelationship ?? []),
                                                {
                                                    _tempId: GuidUtils.Generate(),
                                                    projectId,
                                                    contextNodeId,
                                                    relationshipId,
                                                },
                                            ],
                                        };
                                    }
                                    setContact(loaded);
                                    setExistingContact(ObjectUtils.Clone(loaded));
                                    setEditMode(true);
                                }
                            );
                            return;
                        }
                    }

                    setContact(loaded);
                    if (editOnOpen) {
                        setExistingContact(ObjectUtils.Clone(loaded));
                        setEditMode(true);
                    } else {
                        setEditMode(false);
                    }
                }
            );
        }
    };

    const enterEditMode = () => {
        //set edit mode state
        setEditMode(true);
        setExistingContact(ObjectUtils.Clone(contact));
    };

    const exitEditMode = () => {
        //exit edit mode. Either replace with the old version, or change the page back.
        if(existingContact) {
            setEditMode(false);
            setContact(ObjectUtils.Clone(existingContact));
            setExistingContact(null);
        } else {
            let newPath = window.location.pathname.replace("/contacts/new", "/contacts");
            changePage(newPath);
        }
    };

    const updateContact = (contact) => {
        setContact(contact);
    };

    const saveContact = () => {
        //save the contact against the api.
        if(contact.stakeholderId) {
            makePageRequest(
                () => ContactService.UpdateContact(contact),
                (x) => {
                    setEditMode(false);
                    setContact(x.result);
                }
            );
        } else {
            makePageRequest(
                () => ContactService.CreateContact(contact),
                (x) => {
                    setEditMode(false);
                    setContact(x.result);
                    let newPath = window.location.pathname.replace("new", x.result.stakeholderId);
                    changePage(newPath);
                }
            );
        }
    };

    const saveAndClose = () => {
        if(contact.stakeholderId) {
            makePageRequest(
                () => ContactService.UpdateContact(contact),
                () => window.close()
            );
        } else {
            makePageRequest(
                () => ContactService.CreateContact(contact),
                () => window.close()
            );
        }
    };

    const deleteContact = () => {
        //make delete ajax request
        makePageRequest(
            () => ContactService.DeleteContact(contact.stakeholderId),
            () => changePage("/")
        );
    };

    const canEdit = () => {
        if(contact) {
            if(permission('update_project')) {
                // if the user can edit projects, then they can edit contacts.
                // because contacts can be assigned to projects from the project edit page.
                // and after they are assigned, they can be edited from the contact page.
                // so lets just give them access.
                // This also prevents the issue where a project manager cannot edit a contact
                return true;
            }
            if(permission('update_contact')) {
                if(contact.ProjectStakeholderRelationship?.length) {
                    return contact.ProjectStakeholderRelationship.some((x) =>
                        editContextNodeId(x.contextNodeId)
                    );
                } else {
                    //if no projects are assigned users who can edit outside of assignments can still edit. so lets give them access.
                    return editContextNodeId(null);
                }
            }
        }
        return false;
    };

    const canDelete = () => {
        if(contact) {
            if(permission('delete_contact')) {
                if(contact.ProjectStakeholderRelationship?.length) {
                    return contact.ProjectStakeholderRelationship.some((x) =>
                        editContextNodeId(x.contextNodeId)
                    );
                } else {
                    //if no projects are assigned users who can edit outside of assignments can still edit. so lets give them access.
                    return editContextNodeId(null);
                }
            }
        }

        return false;
    };

    const getContactName = () => {
        let contactName = ContactService.GetTitle(contact);
        return contactName !== "" ? contactName : "New Contact";
    };

    // don't render anything until we are loaded.
    if(contact === null) {
        return null;
    }

    return <Container className={classNames}>
        <Row>
            <ConfirmationDialog
                Title="Confirmation"
                Body="Are you sure you want to delete this contact?"
                Display={deleteDialog}
                OnClose={() => setDeleteDialog(false)}
                OnConfirm={() => deleteContact()}
            />
            <Col xs={12} className="sb-sticky-header">
                <h2 className="atomic-sectionheader">
                    <div className="float-right btn-edit">
                        {
                            editMode ? <React.Fragment>
                                    {fromSurvey && (
                                        <Button
                                            variant="save"
                                            onClick={() => saveAndClose()}
                                            disabled={!isValid}
                                        >
                                            Save and Return
                                        </Button>
                                    )}
                                    <Button
                                        variant="save"
                                        onClick={() => saveContact()}
                                        disabled={!isValid}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="cancel"
                                        onClick={() => exitEditMode()}
                                    >
                                        Cancel
                                    </Button>
                                    {
                                        contactId !== 'new' && canDelete() ?
                                            <Button
                                                variant="delete"
                                                onClick={() => setDeleteDialog(true)}
                                            >
                                                Delete
                                            </Button> :
                                            null
                                    }
                                </React.Fragment> :
                                <React.Fragment>
                                    {fromSurvey && (
                                        <Button
                                            variant="link"
                                            onClick={() => window.close()}>
                                            &larr; Return to Survey
                                        </Button>
                                    )}
                                    {canEdit() && (
                                        <Button
                                            variant="link"
                                            onClick={() => enterEditMode()}>
                                            <Glyphicon glyph="pencil">Edit</Glyphicon>
                                        </Button>
                                    )}
                                </React.Fragment>
                        }
                    </div>
                    {getContactName()}
                </h2>
            </Col>
            <Col xs={12}>
                {
                    contact ?
                        <GenericModelViewer
                            IsNewModel={contactId === "new"}
                            EditMode={editMode}
                            Data={contact}
                            Update={updateContact}
                            OnValidationUpdate={setValidations}
                            Model={model}
                        />:
                        null
                }
            </Col>
        </Row>
    </Container>;
};
